/**
 * AR07 copy (one group per control; a ticket edits only its own file). Core: ordinary customer
 * receivables with functional-currency FX carrying and cutoff chart history, plus separate
 * contract-asset, customer-advance, and suffered-withholding bridges.
 */
export const ar07PtBr = {
  title: 'Auxiliar de clientes x razão',
  description: 'Saldos por cliente e conta comparados ao razão na mesma data.',
  prerequisite: '',
  reasons: {
    AUXILIARY_ABOVE_GL: 'Auxiliar acima do razão',
    AUXILIARY_BELOW_GL: 'Auxiliar abaixo do razão',
    UNMATCHED_MANUAL_GL_LINE: 'Lançamento manual sem origem em AR',
    COVERAGE_LIMIT_DEBIT_NOTE_OPEN: 'Nota de débito em aberto não coberta',
    COVERAGE_LIMIT_UNSUPPORTED_INVOICE_TYPE: 'Tipo de fatura não coberto (liquidação agregada)',
    COVERAGE_LIMIT_FOREIGN_CURRENCY: 'Fatura em moeda estrangeira não coberta',
    COVERAGE_LIMIT_FX_EXPOSURE_MISSING: 'Histórico de exposição cambial ausente',
    COVERAGE_LIMIT_FX_CURRENCY_MISMATCH: 'Moeda da exposição ou liquidação cambial não compatível',
    COVERAGE_LIMIT_FX_MOVEMENT_UNVERIFIED: 'Movimento cambial sem vínculo verificável com o razão',
    COVERAGE_LIMIT_FX_REVERSAL_UNVERIFIED:
      'Estorno cambial sem movimento compensatório verificável',
    COVERAGE_LIMIT_FX_ACCOUNT_MISMATCH:
      'Conta cambial não corresponde à conta histórica do cliente',
    COVERAGE_LIMIT_FX_UNITS_MISMATCH:
      'Unidades em moeda estrangeira divergem dos títulos e recebimentos',
    COVERAGE_LIMIT_HISTORICAL_CHART_UNCOVERED:
      'Plano de contas histórico sem mapeamento verificável na data',
    COVERAGE_LIMIT_VOID_TIMING_UNKNOWN: 'Data do cancelamento não determinada',
    COVERAGE_LIMIT_UNSUPPORTED_PATH: 'Título com crédito ou baixa não vinculada à parcela',
    COVERAGE_LIMIT_NO_RECEIVABLE_ACCOUNT_MAPPED: 'Cliente sem conta de recebíveis configurada',
    COVERAGE_LIMIT_SCOPE_UNRESOLVED: 'Escopo contábil não resolvido',
    COVERAGE_LIMIT_RECEIVABLE_ACCOUNT_NOT_FOUND: 'Conta de recebíveis não encontrada',
    COVERAGE_LIMIT_GL_VISIBILITY: 'Contas protegidas impedem a conciliação completa',
    COVERAGE_LIMIT_SHARED_CUSTOMER_RECEIVABLE_ACCOUNT:
      'Conta compartilhada não permite atribuir o saldo por cliente',
    COVERAGE_LIMIT_INVOICE_STATUS_HISTORY_UNSUPPORTED:
      'Histórico de situação da fatura não permite reconstruir a data',
    COVERAGE_LIMIT_SCHEDULE_CHANGED_AFTER_CUTOFF:
      'Parcela alterada após a data de corte sem histórico anterior',
    COVERAGE_LIMIT_HISTORICAL_ORIGIN_UNVERIFIED:
      'Vínculo histórico da fatura ainda não foi verificado',
    COVERAGE_LIMIT_HISTORICAL_OPENING_UNVERIFIED:
      'Saldo inicial importado ainda não foi verificado',
    UNATTRIBUTED_GL_BALANCE: 'Saldo de recebíveis sem cliente atribuído',
    CUSTOMER_ADVANCE_AUXILIARY_ABOVE_GL: 'Adiantamentos de clientes acima do razão',
    CUSTOMER_ADVANCE_AUXILIARY_BELOW_GL: 'Adiantamentos de clientes abaixo do razão',
    CONTRACT_ASSET_AUXILIARY_ABOVE_GL: 'Ativos de contrato acima do razão',
    CONTRACT_ASSET_AUXILIARY_BELOW_GL: 'Ativos de contrato abaixo do razão',
    SUFFERED_WITHHOLDING_AUXILIARY_ABOVE_GL: 'Retenções sofridas acima do razão',
    SUFFERED_WITHHOLDING_AUXILIARY_BELOW_GL: 'Retenções sofridas abaixo do razão',
    COVERAGE_LIMIT_AR07_ADVANCE_AUXILIARY_UNAVAILABLE: 'Auxiliar de adiantamentos indisponível',
    COVERAGE_LIMIT_AR07_ADVANCE_UNSUPPORTED_EVIDENCE: 'Evidência de adiantamento não verificável',
    COVERAGE_LIMIT_AR07_WITHHOLDING_AUXILIARY_UNAVAILABLE: 'Auxiliar de retenções indisponível',
    COVERAGE_LIMIT_AR07_WITHHOLDING_UNSUPPORTED_EVIDENCE: 'Evidência de retenção não verificável',
    COVERAGE_LIMIT_AR07_WITHHOLDING_GL_ORIGIN_UNSUPPORTED:
      'Lançamento de retenção sem origem verificável',
    COVERAGE_LIMIT_AR07_CONTRACT_ASSET_UNSUPPORTED_EVIDENCE:
      'Evidência de ativo de contrato não verificável',
    COVERAGE_LIMIT_AR07_CONTRACT_ASSET_OBLIGATION_UNAVAILABLE:
      'Obrigação do ativo de contrato indisponível',
    COVERAGE_LIMIT_AR07_NATURE_SCOPE_UNRESOLVED: 'Escopo contábil das naturezas não resolvido',
    COVERAGE_LIMIT_AR07_NATURE_CURRENCY_UNRESOLVED: 'Moeda funcional das naturezas não resolvida',
    COVERAGE_LIMIT_AR07_NATURE_GL_VISIBILITY:
      'Contas protegidas impedem a conciliação das naturezas',
  },
  reviewRequired:
    'Revise as diferenças e os caminhos de origem sem evidência suficiente antes do fechamento.',
  breakdownTruncated:
    'Mostrando apenas as 100 primeiras pendências; a distribuição por motivo não é exibida.',
  openCustomer: 'Abrir cliente',
  openInvoice: 'Abrir fatura',
  openContract: 'Abrir contrato',
  evidence: {
    book: 'Livro',
    accounts: 'Contas',
    journals: 'Lançamentos',
  },
  listTruncated: 'Exibindo {{shown}} de {{total}} pendências.',
};

export const ar07En: typeof ar07PtBr = {
  title: 'Customer subledger vs. GL',
  description: 'Balances by customer and account compared with the GL at the same date.',
  prerequisite: '',
  reasons: {
    AUXILIARY_ABOVE_GL: 'Auxiliary above the GL',
    AUXILIARY_BELOW_GL: 'Auxiliary below the GL',
    UNMATCHED_MANUAL_GL_LINE: 'Manual line with no AR origin',
    COVERAGE_LIMIT_DEBIT_NOTE_OPEN: 'Open debit note not covered',
    COVERAGE_LIMIT_UNSUPPORTED_INVOICE_TYPE: 'Invoice type not covered (settlement aggregate)',
    COVERAGE_LIMIT_FOREIGN_CURRENCY: 'Foreign-currency invoice not covered',
    COVERAGE_LIMIT_FX_EXPOSURE_MISSING: 'Foreign-exchange exposure history is missing',
    COVERAGE_LIMIT_FX_CURRENCY_MISMATCH: 'Exposure or settlement currency is unsupported',
    COVERAGE_LIMIT_FX_MOVEMENT_UNVERIFIED: 'FX movement cannot be verified against the GL',
    COVERAGE_LIMIT_FX_REVERSAL_UNVERIFIED: 'FX reversal has no verifiable compensating movement',
    COVERAGE_LIMIT_FX_ACCOUNT_MISMATCH:
      'FX carrying account differs from the customer historical account',
    COVERAGE_LIMIT_FX_UNITS_MISMATCH: 'Foreign units differ from dated invoices and receipts',
    COVERAGE_LIMIT_HISTORICAL_CHART_UNCOVERED:
      'Historical chart mapping cannot be verified at cutoff',
    COVERAGE_LIMIT_VOID_TIMING_UNKNOWN: 'Cancellation date not determined',
    COVERAGE_LIMIT_UNSUPPORTED_PATH:
      'Title with a credit or settlement not tied to the installment',
    COVERAGE_LIMIT_NO_RECEIVABLE_ACCOUNT_MAPPED: 'Customer has no receivable account configured',
    COVERAGE_LIMIT_SCOPE_UNRESOLVED: 'Accounting scope not resolved',
    COVERAGE_LIMIT_RECEIVABLE_ACCOUNT_NOT_FOUND: 'Receivable account not found',
    COVERAGE_LIMIT_GL_VISIBILITY: 'Protected accounts prevent a complete reconciliation',
    COVERAGE_LIMIT_SHARED_CUSTOMER_RECEIVABLE_ACCOUNT:
      'Shared account balance cannot be attributed by customer',
    COVERAGE_LIMIT_INVOICE_STATUS_HISTORY_UNSUPPORTED:
      'Invoice status history cannot be reconstructed at the cutoff',
    COVERAGE_LIMIT_SCHEDULE_CHANGED_AFTER_CUTOFF:
      'Installment changed after cutoff without prior history',
    COVERAGE_LIMIT_HISTORICAL_ORIGIN_UNVERIFIED: 'Historical invoice origin link is not verified',
    COVERAGE_LIMIT_HISTORICAL_OPENING_UNVERIFIED: 'Imported opening balance is not verified',
    UNATTRIBUTED_GL_BALANCE: 'Receivable balance has no attributed customer',
    CUSTOMER_ADVANCE_AUXILIARY_ABOVE_GL: 'Customer advances above the GL',
    CUSTOMER_ADVANCE_AUXILIARY_BELOW_GL: 'Customer advances below the GL',
    CONTRACT_ASSET_AUXILIARY_ABOVE_GL: 'Contract assets above the GL',
    CONTRACT_ASSET_AUXILIARY_BELOW_GL: 'Contract assets below the GL',
    SUFFERED_WITHHOLDING_AUXILIARY_ABOVE_GL: 'Suffered withholding above the GL',
    SUFFERED_WITHHOLDING_AUXILIARY_BELOW_GL: 'Suffered withholding below the GL',
    COVERAGE_LIMIT_AR07_ADVANCE_AUXILIARY_UNAVAILABLE: 'Customer advance auxiliary is unavailable',
    COVERAGE_LIMIT_AR07_ADVANCE_UNSUPPORTED_EVIDENCE:
      'Customer advance evidence cannot be verified',
    COVERAGE_LIMIT_AR07_WITHHOLDING_AUXILIARY_UNAVAILABLE: 'Withholding auxiliary is unavailable',
    COVERAGE_LIMIT_AR07_WITHHOLDING_UNSUPPORTED_EVIDENCE: 'Withholding evidence cannot be verified',
    COVERAGE_LIMIT_AR07_WITHHOLDING_GL_ORIGIN_UNSUPPORTED:
      'Withholding journal has no verifiable origin',
    COVERAGE_LIMIT_AR07_CONTRACT_ASSET_UNSUPPORTED_EVIDENCE:
      'Contract asset evidence cannot be verified',
    COVERAGE_LIMIT_AR07_CONTRACT_ASSET_OBLIGATION_UNAVAILABLE:
      'Contract asset obligation is unavailable',
    COVERAGE_LIMIT_AR07_NATURE_SCOPE_UNRESOLVED: 'Nature accounting scope cannot be resolved',
    COVERAGE_LIMIT_AR07_NATURE_CURRENCY_UNRESOLVED: 'Nature functional currency cannot be resolved',
    COVERAGE_LIMIT_AR07_NATURE_GL_VISIBILITY: 'Protected accounts prevent nature reconciliation',
  },
  reviewRequired: 'Review differences and source paths without sufficient evidence before closing.',
  breakdownTruncated: 'Showing only the first 100 findings; the breakdown by reason is hidden.',
  openCustomer: 'Open customer',
  openInvoice: 'Open invoice',
  openContract: 'Open contract',
  evidence: {
    book: 'Book',
    accounts: 'Accounts',
    journals: 'Journals',
  },
  listTruncated: 'Showing {{shown}} of {{total}} findings.',
};
